import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Package,
  FileText,
  Settings,
  BarChart3,
  Edit3,
  Truck,
  Box,
  LogOut,
  ExternalLink,
  User,
  ChevronLeft,
  ChevronRight,
  Target,
  CheckSquare,
  UserCircle2,
  Mail,
  Shield,
  ClipboardCheck,
  ScanSearch,
  Handshake,
  FileBox,
  Briefcase
} from 'lucide-react';
import './AdminSidebar.css';

const AdminSidebar = () => {
  const { t } = useTranslation();
  const { admin, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === 'true'
  );

  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    localStorage.setItem('sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  // Clean up body class on unmount
  useEffect(() => {
    return () => document.body.classList.remove('sidebar-collapsed');
  }, []);
  
  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: t('admin.nav.dashboard'), color: '#3b82f6' },
    // CRM section
    { path: '/admin/customers', icon: UserCircle2, label: 'Customers', color: '#a78bfa' },
    { path: '/admin/pipeline', icon: Target, label: 'Pipeline', color: '#f97316' },
    { path: '/admin/tasks', icon: CheckSquare, label: 'Tasks', color: '#f59e0b' },
    // Malta Track C
    { path: '/admin/compliance', icon: ClipboardCheck, label: 'COI / Compliance', color: '#ef4444' },
    { path: '/admin/screens', icon: ScanSearch, label: 'Obsolescence Screens', color: '#14b8a6' },
    { path: '/admin/partners', icon: Handshake, label: 'Partners', color: '#22c55e' },
    { path: '/admin/part-files', icon: FileBox, label: 'Part Files', color: '#6366f1' },
    { path: '/admin/engagements', icon: Briefcase, label: 'Engagements', color: '#eab308' },
    { path: '/admin/leads', icon: Users, label: t('admin.nav.leads'), color: '#8b5cf6' },
    { path: '/admin/orders', icon: Package, label: t('admin.nav.orders'), color: '#10b981' },
    { path: '/admin/quotes', icon: FileText, label: t('admin.nav.quotes'), color: '#f59e0b' },
    { path: '/admin/content', icon: Edit3, label: t('admin.nav.content'), color: '#ec4899' },
    { path: '/admin/products', icon: Box, label: t('admin.nav.products'), color: '#06b6d4' },
    { path: '/admin/shipping', icon: Truck, label: t('admin.nav.shipping'), color: '#a855f7' },
    { path: '/admin/analytics', icon: BarChart3, label: t('admin.nav.analytics'), color: '#f97316' },
    { path: '/admin/email-templates', icon: Mail, label: 'Email Templates', color: '#0ea5e9' },
    { path: '/admin/audit', icon: Shield, label: 'Audit Log', color: '#71717a' },
    { path: '/admin/automation', icon: () => <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>🤖</span>, label: 'Automazione', color: '#8b5cf6' },
    { path: '/admin/agents', icon: () => <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>🧠</span>, label: 'Agent Fleet', color: '#a855f7' },
    { path: '/admin/agentic', icon: () => <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>🛰️</span>, label: 'Agentic Console', color: '#06b6d4' },
    { path: '/admin/settings', icon: Settings, label: t('admin.nav.settings'), color: '#6b7280' }
  ];

  return (
    <aside className={`admin-sidebar${collapsed ? ' sidebar-collapsed' : ''}`}>
      <div className="sidebar-header">
        <img src="/logos/meccaflex-isometric-logo.svg" alt="MeccaFlex" />
        <h2 className="sidebar-logo-text">MeccaFlex</h2>
        <button
          className="sidebar-toggle-btn"
          onClick={() => setCollapsed(prev => !prev)}
          aria-label={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
          title={collapsed ? 'Espandi' : 'Comprimi'}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
            title={collapsed ? item.label : undefined}
          >
            <div className="sidebar-icon" style={{ background: `${item.color}20` }}>
              <item.icon size={20} color={item.color} />
            </div>
            <span className="nav-text">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        <a href="/" target="_blank" rel="noopener noreferrer" className="sidebar-back-link" title={collapsed ? 'Vai al sito' : undefined}>
          <ExternalLink size={14} />
          <span className="sidebar-back-text">Vai al sito</span>
        </a>
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">
            <User size={16} color="#f97316" />
          </div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{admin?.name || 'Admin'}</span>
            <span className="sidebar-user-role">Amministratore</span>
          </div>
        </div>
        <button
          onClick={logout}
          className="sidebar-logout-btn"
          aria-label="Esci"
          title={collapsed ? 'Esci' : undefined}
        >
          <LogOut size={16} />
          <span className="sidebar-logout-text">Esci</span>
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
